/**
 * Integrytas Group - Common Security Module (CSM)
 * Filtered Modal Multi-Select.
 *
 * Replaces every dropdown lookup. Per Integrytas standard:
 *  - Modal opens on trigger click.
 *  - If options.length > 10, an internal search bar is rendered.
 *  - Optional per-field FACETS (§17.B5): pass `facets` plus `facet_values` on
 *    each option and the picker offers the same field filtering its backing
 *    list page does, not just free-text search. Inert when omitted.
 *  - Selection logic always keys on `internal_code`; display strings are
 *    purely for the UI and arrive pre-translated from the *_tl tables.
 *
 * Generic over the option type so it can drive role-to-user assignment,
 * page selection, language picker, etc.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FacetFilter } from './FacetFilter';

export interface MultiSelectOption {
  internal_code: string;
  display_text: string;
  description?: string | null;
  is_active?: boolean;
  /**
   * Optional per-option facet field values, e.g. `{ status: 'Active', currency: 'USD — US Dollar' }`.
   * Lets a large-list picker offer the same per-field facets its backing list page
   * offers (§17.B5), not just free-text search.
   *
   * These strings are what the facet chip both FILTERS and LABELS by, so they must
   * be the RESOLVED display value, never a raw code or id (§17.B4) — a facet whose
   * option label is a bare code is as non-conformant as a grid cell showing one.
   * Where the code is itself meaningful to users (currency, country), pass
   * `CODE — Name`.
   *
   * One string per key: a facet cannot express a multi-valued attribute. Where an
   * option legitimately holds several values for a field (several roles, several
   * types), leave that field out rather than passing the first — a chip matching
   * only the first value silently hides the rest.
   */
  facet_values?: Record<string, string>;
}

/** A filterable field on the option set, matched against `facet_values[key]`. */
export interface SelectFacetDef {
  key: string;
  label: string; // already localized
}

export interface FilteredModalMultiSelectProps {
  label: string;                                      // already localized
  options: MultiSelectOption[];
  selectedCodes: string[];                            // controlled
  onChange: (codes: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  searchThreshold?: number;                           // default 10
  /** Optional facets mirroring this object's list-page filter bar. Inert when omitted. */
  facets?: SelectFacetDef[];
}

export function FilteredModalMultiSelect({
  label,
  options,
  selectedCodes,
  onChange,
  placeholder,
  disabled = false,
  searchThreshold = 10,
  facets,
}: FilteredModalMultiSelectProps) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('common.select.placeholder', 'Select...');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState<string[]>(selectedCodes);
  const [facetSelections, setFacetSelections] = useState<Record<string, string[]>>({});

  const showSearch = options.length > searchThreshold;

  // Two stages, deliberately: the facet CHIPS are built from `searched`, so the
  // values on offer narrow with the text query, while `filtered` applies the
  // chosen facets on top. Building chips from `filtered` instead would let a
  // selection remove its own chip from the list.
  const searched = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter(o =>
      o.display_text.toLowerCase().includes(q) ||
      o.internal_code.toLowerCase().includes(q) ||
      (o.description ?? '').toLowerCase().includes(q),
    );
  }, [options, query]);

  const filtered = useMemo(() => {
    const activeFacetKeys = Object.keys(facetSelections).filter(k => facetSelections[k]?.length);
    if (activeFacetKeys.length === 0) return searched;
    return searched.filter(o => activeFacetKeys.every(k => facetSelections[k].includes(o.facet_values?.[k] ?? '')));
  }, [searched, facetSelections]);

  const facetOptionsFor = useCallback((key: string) => {
    const seen = new Map<string, number>();
    for (const o of searched) {
      const v = o.facet_values?.[key];
      if (v == null) continue;
      seen.set(v, (seen.get(v) ?? 0) + 1);
    }
    return Array.from(seen.entries()).map(([value, count]) => ({ value, label: value, count }));
  }, [searched]);

  const cancel = useCallback(() => setOpen(false), []);

  const openModal = () => {
    if (disabled) return;
    setDraft(selectedCodes);
    setQuery('');
    setFacetSelections({});
    setOpen(true);
  };

  const toggleCode = (code: string) => {
    setDraft(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code],
    );
  };

  const apply = () => {
    onChange(draft);
    setOpen(false);
  };

  // Intercept ESC in capture phase so it closes only this picker,
  // not the parent Modal whose handler runs in the bubble phase.
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        cancel();
      }
    };
    document.addEventListener('keydown', h, true);
    return () => document.removeEventListener('keydown', h, true);
  }, [open, cancel]);

  // Render selected chips for the trigger
  const selectedLabels = options
    .filter(o => selectedCodes.includes(o.internal_code))
    .map(o => o.display_text);

  return (
    <div className="w-full">
      <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">
        {label}
      </label>

      <button
        type="button"
        onClick={openModal}
        disabled={disabled}
        className={
          'flex min-h-[32px] w-full items-center justify-between rounded border border-csm-border bg-white px-2 py-1 text-left text-csm-md ' +
          (disabled ? 'cursor-not-allowed bg-csm-surface-alt text-csm-watermark' : 'hover:border-csm-watermark')
        }
      >
        {selectedLabels.length === 0 ? (
          <span className="text-csm-watermark">{resolvedPlaceholder}</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {selectedLabels.map(t => (
              <span key={t} className="rounded border border-csm-border bg-csm-surface-alt px-1.5 py-0.5 text-csm-xs">
                {t}
              </span>
            ))}
          </span>
        )}
        <span aria-hidden className="ml-2 text-csm-watermark">v</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4">
          <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded border border-csm-border bg-white shadow-xl">
            <header className="flex shrink-0 items-center justify-between border-b border-csm-border px-3 py-2">
              <h2 className="text-csm-md font-semibold text-csm-ink">{label}</h2>
              <button
                type="button"
                onClick={cancel}
                aria-label={t('common.close', 'Close')}
                className="text-csm-muted hover:text-csm-ink"
              >
                x
              </button>
            </header>

            {showSearch && (
              <div className="shrink-0 border-b border-csm-border p-2">
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={t('common.search', 'Search...')}
                  className="h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
                  autoFocus
                />
              </div>
            )}

            {facets && facets.length > 0 && (
              <div className="flex shrink-0 flex-wrap gap-1 border-b border-csm-border p-2">
                {facets.map(f => (
                  <FacetFilter
                    key={f.key}
                    label={f.label}
                    options={facetOptionsFor(f.key)}
                    selected={facetSelections[f.key] ?? []}
                    onChange={values => setFacetSelections(prev => ({ ...prev, [f.key]: values }))}
                  />
                ))}
              </div>
            )}

            <ul className="min-h-0 flex-1 overflow-y-auto">
              {filtered.length === 0 && (
                <li className="px-3 py-3 text-csm-sm italic text-csm-watermark">
                  {t('common.no_matches', 'No matches.')}
                </li>
              )}
              {filtered.map(opt => {
                const checked = draft.includes(opt.internal_code);
                const inactive = opt.is_active === false;
                return (
                  <li
                    key={opt.internal_code}
                    className={
                      'flex cursor-pointer items-center gap-2 border-b border-csm-border-soft px-3 py-2 text-csm-md ' +
                      (inactive ? 'text-csm-watermark' : 'hover:bg-csm-surface-alt')
                    }
                    onClick={() => !inactive && toggleCode(opt.internal_code)}
                  >
                    <input
                      type="checkbox"
                      readOnly
                      checked={checked}
                      disabled={inactive}
                      className="h-4 w-4 accent-gray-700"
                    />
                    <div className="flex-1">
                      <div className="font-medium">{opt.display_text}</div>
                      {opt.description && (
                        <div className="text-csm-xs text-csm-muted">{opt.description}</div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>

            <footer className="flex shrink-0 items-center justify-between border-t border-csm-border bg-csm-surface-alt px-3 py-2">
              <span className="text-csm-xs text-csm-muted">
                {t('common.multiselect.n_selected', '{{count}} selected', { count: draft.length })}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={cancel}
                  className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  type="button"
                  onClick={apply}
                  className="rounded border border-sidebar bg-sidebar px-3 py-1 text-csm-sm text-white hover:bg-sidebar-alt"
                >
                  {t('common.apply', 'Apply')}
                </button>
              </div>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
